/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	offEvent,
	onEvent,
	TGetSimpleEventEmitterDataType,
	TSimpleEventEmitter
} from "@adapptio/lib-shared";
import React, { useEffect } from "react";
import { useLatest } from ".";

/**
 * Binds event handler to given event emitter
 *
 * @param eventEmitter Simple event emitter
 * @param eventHandler Event handler to bind
 * @param deps React dependency list
 * @param active If event should be bound
 * @param callHandlerOnMount If event handler should be called on mount (will be called without event data)
 */
export function useSimpleEvent<TEmitter extends TSimpleEventEmitter<unknown> = TSimpleEventEmitter<unknown>>(
	eventEmitter: TEmitter,
	eventHandler: (data: TGetSimpleEventEmitterDataType<TEmitter>) => void,
	deps: React.DependencyList = [],
	active = true,
	callHandlerOnMount = false
): void {
	const handler = useLatest(eventHandler);

	useEffect(() => {
		if (!active || !eventEmitter) {
			return;
		}

		const eventCb: typeof eventHandler = (...args) => handler.current(...args);

		onEvent(eventEmitter, eventCb);

		if (callHandlerOnMount) {
			eventCb(undefined);
		}

		return () => {
			offEvent(eventEmitter, eventCb);
		};
	}, [ eventEmitter, active ].concat(deps));
}
